import { Backdrop } from '@room-match/shared-ui-components';
import InfoContainer from 'components/MyInfo/InfoContainer/InfoContainer';
import { Fragment, lazy, Suspense, useMemo, useState } from 'react';
import { useFormModal } from 'shared/hooks/PetsCars/useFormModal';
import { useSubmitForm } from 'shared/hooks/PetsCars/useSubmitForm';
import { useViewMode } from 'shared/hooks/PetsCars/useViewMode';
import { ICarsProps, ITalentCars } from 'shared/interfaces/ICars';
import { ILoadingDataProps } from 'shared/interfaces/ILoadingData';
import { useAppSelector } from 'shared/redux/store';
import CarList from './CarList/CarList';

const CarAddEditModal = lazy(() => import('./CarAddEditModal/CarAddEditModal'));
const CarViewModal = lazy(() => import('./CarViewModal/CarViewModal'));

const Cars = () => {
  // Get the talent data
  const { user: userState } = useAppSelector((state) => state);

  // Get the Cars Data
  const carsData: ITalentCars[] = useMemo(
    () =>
      userState && userState?.user?.attributes?.cars && Array.isArray(userState?.user?.attributes?.cars)
        ? userState?.user?.attributes?.cars
        : [],
    [userState],
  );

  // State to Open the Add Car modal or Edit
  const [loadingDataInfo, setLoadingDataInfo] = useState<ILoadingDataProps>({
    data: null,
  });

  // Preferences for saving the View Mode
  const preferences = useMemo(() => {
    return userState && userState?.user?.attributes?.preferences ? userState?.user?.attributes?.preferences : null;
  }, [userState]);

  // View Mode Functionality
  const { view, handleView, viewModeLoading } = useViewMode({
    type: 'cars',
    preferences,
  });

  // Loading UI, for displaying the new car item
  const handleLoadingDataInfo = (data: ICarsProps['newCarProps']) => {
    setLoadingDataInfo({
      data: [
        {
          name: '',
          details: data?.manufacturer?.name + ' ' + data?.model?.name,
        },
        {
          name: 'Type',
          details: data?.singleVariant?.type,
        },
        {
          name: 'Year',
          details: data?.variant?.year || '',
        },
      ],
    });
  };

  const { handleAddItem, handleEditItem, handleDeleteItem, progress } = useSubmitForm<
    ITalentCars,
    ICarsProps['newCarProps']
  >({
    data: carsData,
    handleSavingInfo: handleLoadingDataInfo,
    type: 'cars',
  });

  const {
    openFormModal,
    openViewModal,
    formMode,
    selectedItem,
    NotificationModal,
    isOpen,
    ConfirmationModal,
    isModalDeleteOpen,
    handleOpenFormModal,
    handleDeleteModal,
    handleViewModal,
  } = useFormModal({ progress, title: 'Car' });
  return (
    <Fragment>
      {isOpen ? <NotificationModal /> : <Fragment />}
      {viewModeLoading ? <Backdrop isLoading={viewModeLoading} /> : <Fragment />}
      <Suspense fallback={<Backdrop isLoading={true} />}>
        {openFormModal && formMode ? (
          <CarAddEditModal
            openModal={openFormModal}
            handleOpenModal={handleOpenFormModal}
            data={formMode === 'add' ? undefined : carsData[selectedItem.edit]}
            mode={formMode}
            handleAddCar={handleAddItem}
            handleEditCar={handleEditItem}
          />
        ) : (
          <Fragment />
        )}
        {openViewModal ? (
          <CarViewModal
            data={carsData[selectedItem.view]}
            openDialog={openViewModal}
            handleDialog={() => handleViewModal(0)}
          />
        ) : (
          <Fragment />
        )}
      </Suspense>
      {isModalDeleteOpen ? (
        <ConfirmationModal
          onConfirm={() => {
            handleDeleteItem({ id: carsData[selectedItem.delete]?.id });
            setLoadingDataInfo({ data: null });
          }}
        />
      ) : (
        <Fragment />
      )}

      <InfoContainer
        title="Cars"
        button_text={'Add New Car'}
        viewMode={view}
        onViewClick={handleView}
        onAddFormClick={() => handleOpenFormModal('add')}
      >
        <CarList
          dataList={carsData}
          loadingDataInfo={loadingDataInfo}
          isSaving={progress === 'saving'}
          isEditing={progress === 'editing'}
          isDeleting={progress === 'deleting'}
          selectedEditedItem={selectedItem.edit}
          selectedDeletedItem={selectedItem.delete}
          handleOpenFormModal={handleOpenFormModal}
          handleDeleteModal={handleDeleteModal}
          handleViewModal={handleViewModal}
          variant={view}
        />
      </InfoContainer>
    </Fragment>
  );
};

export default Cars;
